/**
 * useSetPlateComponent — "This plate is for: <component>" (callable fieldService_gallerySetComponent).
 *
 * Optimistic: the photo's label changes on screen at once, rolled back on error, everything revalidated
 * on settle (the unit record changed too, so the generator detail is refetched). The server decides
 * everything that matters (who may, the compare-and-set on the unit record, a newer plate already on file);
 * its plain-language refusal is shown as the toast.
 */
import { useMutation } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import toast from 'react-hot-toast';
import { functions } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import type { PlateComponentChoice } from '../../lib/fieldService/plateComponents';
import { galleryErrorMessage, useGalleryCache } from './useGeneratorGallery';

export interface SetPlateComponentResult {
  photoId: string;
  component: PlateComponentChoice;
  unitRecord: 'written' | 'already-current' | 'not-touched';
}

const setComponentFn = httpsCallable<
  { organizationId: string; photoId: string; component: PlateComponentChoice },
  SetPlateComponentResult
>(functions, 'fieldService_gallerySetComponent');

export function useSetPlateComponent(generatorDocId: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const cache = useGalleryCache(orgId, generatorDocId);

  return useMutation({
    mutationFn: async ({ photoId, component }: { photoId: string; component: PlateComponentChoice }) =>
      (await setComponentFn({ organizationId: orgId, photoId, component })).data,
    onMutate: async ({ photoId, component }) => {
      const snapshot = await cache.snapshot();
      const label = (row: { id: string }) => row.id === photoId;
      cache.queryClient.setQueryData<Array<{ id: string; component?: string | null; componentSource?: string | null }>>(
        cache.galleryKey,
        (old) => old?.map((r) => (label(r) ? { ...r, component, componentSource: 'person' } : r))
      );
      cache.patchLibraries((row) => (label(row) ? { ...row, component, componentSource: 'person' } : row));
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      cache.restore(ctx?.snapshot);
      toast.error(galleryErrorMessage(err, 'Could not save which component this plate is for.'));
    },
    onSuccess: (result) => {
      toast.success(result.unitRecord === 'not-touched' ? 'Saved: not sure which component' : 'Plate tied to its component');
    },
    onSettled: () => {
      cache.invalidateAll();
      // A tied plate changes every chip on the unit page (proof is read fresh from the gallery rows).
      void cache.queryClient.invalidateQueries({ queryKey: ['plate-proof', orgId, generatorDocId] });
    },
  });
}
